// ===================================================================
// KAIXO - Hook usePlan
// ===================================================================
// Provee a los componentes el plan actual del usuario + helpers:
//
//   const { plan, loading, hasFeature, getLimit, refresh } = usePlan();
//
//   if (!hasFeature('canExportCsv')) return <UpgradeRequired feature="canExportCsv" />;
// ===================================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export function usePlan() {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get('/plans/me');
            setPlan(data);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
            setPlan(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Escucha eventos globales para auto-refrescar tras activación
    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener('kaixo-plan-changed', handler);
        return () => window.removeEventListener('kaixo-plan-changed', handler);
    }, [refresh]);

    const hasFeature = useCallback((key) => {
        if (!plan) return false;
        const v = plan.features?.[key];
        if (typeof v === 'string') return v.length > 0;
        return !!v;
    }, [plan]);

    const getLimit = useCallback((key) => {
        if (!plan) return 0;
        return plan.limits?.[key] ?? 0;
    }, [plan]);

    const isUnlimited = useCallback((key) => {
        const v = getLimit(key);
        return v === -1;
    }, [getLimit]);

    return {
        plan,
        loading,
        error,
        refresh,
        hasFeature,
        getLimit,
        isUnlimited,
        tier: plan?.tier || 'FREE',
        daysLeft: plan?.daysLeft,
        isExpired: plan?.isExpired
    };
}

export default usePlan;
